// src/components/TablaTransportadora.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import styles from "../style/Transportadora.module.css";

const PEDIDOS_URL = "https://marketbusters.pro/Dropdrop/pedidos_listado_novedad_encamino.json";

/* ============ Cookies + fallback localStorage ============ */
const getCookie = (name) => {
  if (typeof document === "undefined") return null;
  const m = document.cookie.match(new RegExp("(^| )" + name + "=([^;]+)"));
  try { return m ? decodeURIComponent(m[2]) : null; } catch { return null; }
};
const setCookie = (name, value, days = 180) => {
  try {
    const encoded = encodeURIComponent(value);
    if (encoded.length > 3800) {
      localStorage.setItem(name, value);
      document.cookie = `${name}=__LS__; path=/; max-age=${days*86400}; samesite=Lax`;
      return;
    }
    document.cookie = `${name}=${encoded}; path=/; max-age=${days*86400}; samesite=Lax`;
  } catch {
    localStorage.setItem(name, value);
  }
};
const getCookieOrLSJson = (name, fallback = {}) => {
  const raw = getCookie(name);
  let txt = raw;
  if (raw === "__LS__" || raw == null) {
    const ls = localStorage.getItem(name);
    if (ls) txt = ls;
  }
  try { return txt ? JSON.parse(txt) : fallback; } catch { return fallback; }
};
const setCookieJson = (name, obj) => setCookie(name, JSON.stringify(obj ?? {}));

/* ============ Utils tabla ============ */
// Limpia "Región Metropolitana de Santiago," y normaliza comuna
const cleanUbigeo = (input) => {
  let s = String(input || "");
  s = s.replace(/regi[oó]n\s+metropolitana\s+de\s+santiago,\s*/iu, "");
  s = s.trim();

  const toTitle = (str) =>
    str
      .toLowerCase()
      .split(/\s+/)
      .map((w) => (w ? w[0].toUpperCase() + w.slice(1) : w))
      .join(" ");

  const plain = s.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
  const fixes = {
    nunoa: "Ñuñoa",
    maipu: "Maipú",
    "san joaquin": "San Joaquín",
    "estacion central": "Estación Central",
    conchali: "Conchalí",
  };
  if (fixes[plain]) return fixes[plain];
  return toTitle(s);
};

// CLP sin decimales
const formatCLP = (val) => {
  const n = Number(String(val).replace(/[^0-9.-]/g, ""));
  if (!isFinite(n)) return String(val ?? "");
  return new Intl.NumberFormat("es-CL", { minimumFractionDigits: 0, maximumFractionDigits: 0 })
    .format(Math.round(n));
};

// Chip Tracking
const TrackingChip = ({ value }) => {
  const v = String(value || "").toLowerCase().trim();
  const base = {
    display: "inline-block",
    padding: "2px 10px",
    borderRadius: "9999px",
    fontSize: 12,
    fontWeight: 800,
    color: "#fff",
    border: "1px solid #000",
    letterSpacing: "0.3px",
    textTransform: "uppercase",
  };
  if (v.includes("novedad")) return <span style={{ ...base, background: "#f59e0b" }}>Novedad</span>;
  if (v.includes("camino"))  return <span style={{ ...base, background: "#2563eb" }}>En camino</span>;
  return <span style={{ ...base, background: "#6b7280" }}>{value || "—"}</span>;
};

/* ============ Columnas (sin Referencia) + Contacto ============ */
const COLUMNS = [
  { key: "nombreNegocioTienda", label: "Tienda" },
  { key: "id", label: "ID" },
  { key: "fechaEmision", label: "Fecha Emisión" },
  { key: "nombreComprador", label: "Comprador" },
  { key: "celularComprador", label: "Celular" },
  { key: "ubigeoComprador", label: "Ubigeo" },
  { key: "direccionComprador", label: "Dirección" },
  { key: "total", label: "Total" },
  { key: "nombreNegocioProveedor", label: "Proveedor" },
  { key: "productoProveedor", label: "Producto (Proveedor)" },
  { key: "totalProveedor", label: "Total Proveedor" },
  { key: "trackindPedido", label: "Tracking" },
  { key: "contacto", label: "Contacto" },
  { key: "idGuia", label: "Guía" },
];

export default function TablaTransportadora({ refreshKey }) {
  const [pedidos, setPedidos] = useState([]);
  const [loadingPedidos, setLoadingPedidos] = useState(false);
  const [errorPedidos, setErrorPedidos] = useState("");

  // UI persistente
  const [colWidths, setColWidths]   = useState(() => getCookieOrLSJson("mb_col_widths", {}));
  const [filters, setFilters]       = useState(() => getCookieOrLSJson("mb_table_filters", {}));
  const [contactMap, setContactMap] = useState(() => getCookieOrLSJson("mb_contact_status", {}));

  useEffect(() => setCookieJson("mb_col_widths", colWidths), [colWidths]);
  useEffect(() => setCookieJson("mb_table_filters", filters), [filters]);
  useEffect(() => setCookieJson("mb_contact_status", contactMap), [contactMap]);

  // Cargar pedidos: al montar y cuando cambia refreshKey
  useEffect(() => {
    let abort = false;
    (async () => {
      try {
        setLoadingPedidos(true);
        setErrorPedidos("");
        const res = await fetch(PEDIDOS_URL, { cache: "no-store", mode: "cors" });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        const items = Array.isArray(data) ? data : Array.isArray(data?.items) ? data.items : [];
        const cleaned = items.map((it) => {
          const row = {};
          COLUMNS.forEach(({ key }) => {
            if (key === "contacto") return; // virtual
            row[key] = it?.[key] ?? "";
          });
          return row;
        });
        if (!abort) setPedidos(cleaned);
      } catch (e) {
        if (!abort) setErrorPedidos(`No se pudo cargar pedidos: ${e.message}`);
      } finally {
        if (!abort) setLoadingPedidos(false);
      }
    })();
    return () => void (abort = true);
  }, [refreshKey]);

  // -------- Column widths (resizable) ----------
  const thRefs = useRef({});
  const resizingRef = useRef(null);
  const onMouseMove = (e) => {
    const r = resizingRef.current;
    if (!r) return;
    const delta = e.clientX - r.startX;
    const newW = Math.max(90, r.startW + delta);
    setColWidths((prev) => ({ ...prev, [r.key]: newW }));
  };
  const onMouseUp = () => {
    resizingRef.current = null;
    window.removeEventListener("mousemove", onMouseMove);
    window.removeEventListener("mouseup", onMouseUp);
  };
  const startResize = (key, e) => {
    const el = thRefs.current[key];
    const startW = el ? el.offsetWidth : (colWidths[key] || 120);
    resizingRef.current = { key, startX: e.clientX, startW };
    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);
    e.preventDefault();
    e.stopPropagation();
  };

  // -------- Valores mostrados por celda (para filtros y selects de opciones) ----------
  const cellDisplay = (key, row) => {
    if (key === "ubigeoComprador") return cleanUbigeo(row[key]);
    if (key === "total" || key === "totalProveedor") return formatCLP(row[key]);
    if (key === "trackindPedido") {
      const v = String(row[key] || "").toLowerCase();
      if (v.includes("novedad")) return "Novedad";
      if (v.includes("camino"))  return "En camino";
      return row[key] || "";
    }
    if (key === "contacto") {
      const current = contactMap[row.id] ?? "no";
      return current === "contactado" ? "Contactado" : "No contactado";
    }
    return row[key] ?? "";
  };

  // -------- Opciones únicas por columna (para SELECTS) ----------
  const optionsByColumn = useMemo(() => {
    const map = {};
    for (const col of COLUMNS) {
      if (col.key === "contacto") {
        map[col.key] = ["Todos", "Contactado", "No contactado"];
        continue;
      }
      if (col.key === "trackindPedido") {
        const s = new Set();
        for (const r of pedidos) {
          const v = cellDisplay(col.key, r);
          if (v) s.add(v);
        }
        s.add("Novedad");
        s.add("En camino");
        map[col.key] = ["Todos", ...Array.from(s).sort((a, b) => a.localeCompare(b, "es"))];
        continue;
      }
      const set = new Set();
      for (const r of pedidos) {
        const v = cellDisplay(col.key, r);
        if (v !== "") set.add(String(v));
      }
      const arr = Array.from(set).sort((a, b) => a.localeCompare(b, "es"));
      map[col.key] = ["Todos", ...arr];
    }
    return map;
  }, [pedidos, contactMap]);

  // -------- Aplicar filtros (por igualdad contra display) ----------
  const filteredPedidos = useMemo(() => {
    const active = Object.fromEntries(
      Object.entries(filters || {}).filter(([_, v]) => v && v !== "Todos")
    );
    if (Object.keys(active).length === 0) return pedidos;

    return pedidos.filter((row) => {
      for (const [key, sel] of Object.entries(active)) {
        if (key === "contacto") {
          const current = contactMap[row.id] ?? "no";
          const asText = current === "contactado" ? "Contactado" : "No contactado";
          if (asText !== sel) return false;
        } else {
          if (cellDisplay(key, row) !== sel) return false;
        }
      }
      return true;
    });
  }, [pedidos, filters, contactMap]);

  // -------- Header con SELECT + handle de resize ----------
  const HeaderCell = ({ col }) => {
    const widthPx = colWidths[col.key];
    return (
      <th
        ref={(el) => (thRefs.current[col.key] = el)}
        className={styles.th}
        style={{
          position: "sticky",
          top: 0,
          zIndex: 2,
          width: widthPx ? `${widthPx}px` : undefined,
          minWidth: widthPx ? `${widthPx}px` : undefined,
          maxWidth: widthPx ? `${widthPx}px` : undefined,
          paddingRight: 18,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8, justifyContent: "space-between" }}>
          <span>{col.label}</span>
          <span
            onMouseDown={(e) => startResize(col.key, e)}
            title="Arrastra para ajustar ancho"
            style={{
              cursor: "col-resize",
              userSelect: "none",
              width: 8,
              height: 18,
              borderLeft: "2px solid rgba(255,255,255,0.2)",
              display: "inline-block",
            }}
          />
        </div>

        {/* SELECT de filtro */}
        <div style={{ marginTop: 6 }}>
          <select
            value={filters[col.key] ?? "Todos"}
            onChange={(e) => setFilters((f) => ({ ...f, [col.key]: e.target.value }))}
            style={{
              width: "100%",
              background: "#0b0e14",
              color: "#e5e7eb",
              border: "1px solid #222633",
              borderRadius: 8,
              padding: "4px 6px",
              fontSize: 12,
            }}
          >
            {(optionsByColumn[col.key] || ["Todos"]).map((opt) => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </select>
        </div>
      </th>
    );
  };

  const columns = COLUMNS;

  return (
    <div className={styles.card}>
      <div className={styles.cardHeader}>
        <h3 className={styles.cardTitle}>Pedidos (Novedad / En camino)</h3>
        <div className={styles.cardSub}>
          {loadingPedidos && <span>Cargando…</span>}
          {errorPedidos && <span className={styles.error}>{errorPedidos}</span>}
        </div>
      </div>

      <div className={styles.tableScroll}>
        <table className={styles.table}>
          <thead className={styles.thead}>
            <tr>
              {columns.map((c) => (
                <HeaderCell key={c.key} col={c} />
              ))}
            </tr>
          </thead>

          <tbody className={styles.tbody}>
            {filteredPedidos.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className={styles.empty}>
                  {loadingPedidos ? "Cargando datos…" : "Sin resultados."}
                </td>
              </tr>
            ) : (
              filteredPedidos.map((row, idx) => (
                <tr key={`${row.id || "row"}-${idx}`} className={styles.tr}>
                  {columns.map((c) => {
                    const widthPx = colWidths[c.key];
                    const cellStyle = {
                      width: widthPx ? `${widthPx}px` : undefined,
                      minWidth: widthPx ? `${widthPx}px` : undefined,
                      maxWidth: widthPx ? `${widthPx}px` : undefined,
                      whiteSpace: "pre-wrap",
                      wordBreak: "break-word",
                    };

                    if (c.key === "celularComprador") {
                      const val = row[c.key];
                      return (
                        <td key={c.key} className={styles.td} style={cellStyle}>
                          {val ? (
                            <a href={`https://wa.me/${val}`} target="_blank" rel="noreferrer" className={styles.link}>
                              {val}
                            </a>
                          ) : ("—")}
                        </td>
                      );
                    }
                    if (c.key === "ubigeoComprador") {
                      return (
                        <td key={c.key} className={styles.td} style={cellStyle}>
                          {cleanUbigeo(row[c.key]) || "—"}
                        </td>
                      );
                    }
                    if (c.key === "total" || c.key === "totalProveedor") {
                      return (
                        <td key={c.key} className={styles.td} style={cellStyle}>
                          {formatCLP(row[c.key])}
                        </td>
                      );
                    }
                    if (c.key === "trackindPedido") {
                      return (
                        <td key={c.key} className={styles.td} style={cellStyle}>
                          <TrackingChip value={row[c.key]} />
                        </td>
                      );
                    }
                    if (c.key === "contacto") {
                      const current = contactMap[row.id] ?? "no";
                      return (
                        <td key={c.key} className={styles.td} style={cellStyle}>
                          <select
                            value={current}
                            onChange={(e) => setContactMap((m) => ({ ...m, [row.id]: e.target.value }))}
                            style={{
                              background: "#0b0e14",
                              color: "#e5e7eb",
                              border: "1px solid #222633",
                              borderRadius: 8,
                              padding: "6px 8px",
                              fontSize: 12,
                            }}
                          >
                            <option value="contactado">Contactado</option>
                            <option value="no">No contactado</option>
                          </select>
                        </td>
                      );
                    }

                    return (
                      <td key={c.key} className={styles.td} style={cellStyle}>
                        {row[c.key] || "—"}
                      </td>
                    );
                  })}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
